// Saf3AI Agent chat panel persistence.
//
// History lives in the AI microservice (per user, per org, per agent mode) so
// it follows the user across browsers and is never readable by the next person
// who signs in on the same browser. Only the Analyst/Helper toggle stays in
// localStorage, under a per-user key (chatStorage.js).

import { getLatestConversation, saveConversation } from './aiExplorerService';
import { clearLegacyChatHistory } from './chatStorage';

export const CHAT_SURFACE = 'chat_panel';
// The backend accepts up to 500; keep the recent tail.
export const MAX_STORED_MESSAGES = 200;

// Only what is needed to re-render a transcript; no transient UI state. The
// opening page greeting is dropped (it names the page the chat started on,
// which is stale on restore); the panel greets afresh.
export const toStoredMessages = (messages = []) => messages
  .filter((m) => m && m.content && !m.loading && !m.isContextSwitch)
  .filter((m, i, all) => all.slice(0, i + 1).some((x) => x.role === 'user'))
  .slice(-MAX_STORED_MESSAGES)
  .map((m) => ({
    role: m.role,
    content: m.content,
    ...(m.error ? { error: true } : {}),
  }));

// Worth saving only once the user has actually asked something.
export const hasUserTurn = (messages = []) => messages.some(
  (m) => m && m.role === 'user' && m.content,
);

export const conversationTitle = (messages = []) => {
  const first = messages.find((m) => m && m.role === 'user' && m.content);
  return first ? String(first.content).slice(0, 80) : 'Saf3AI Agent chat';
};

/**
 * The signed-in user's latest panel conversation for a mode.
 * Resolves to `{ conversationId, messages }`; empty on a new user or on error
 * (the panel then just greets, it never falls back to browser storage).
 */
export const loadChatHistory = async (agentMode) => {
  clearLegacyChatHistory();
  try {
    const res = await getLatestConversation({ surface: CHAT_SURFACE, agent_mode: agentMode });
    const conv = res?.data?.conversation;
    const messages = Array.isArray(conv?.messages)
      ? conv.messages.filter((m) => m && m.content)
      : [];
    return { conversationId: conv?.id || null, messages };
  } catch {
    return { conversationId: null, messages: [] };
  }
};

/** Create or update the conversation; resolves to its id (or null on failure). */
export const persistChatHistory = async ({ conversationId, agentMode, messages }) => {
  const stored = toStoredMessages(messages);
  if (!hasUserTurn(stored)) return conversationId || null;
  try {
    const res = await saveConversation({
      conversation_id: conversationId || undefined,
      surface: CHAT_SURFACE,
      agent_mode: agentMode,
      title: conversationTitle(stored),
      messages: stored,
    });
    return res?.data?.conversation_id || conversationId || null;
  } catch (err) {
    // The conversation vanished or belongs to someone else: start a new one.
    if (conversationId && err?.response?.status === 404) {
      return persistChatHistory({ conversationId: null, agentMode, messages });
    }
    return conversationId || null;
  }
};
